/**
 * The riser diagram of a building (spec 2026-10-19-03 §2, patterns §45): the
 * floors as horizontal bands, every vertical column — downpipe, dry riser,
 * electrical riser, shaft — as a line in its network colour, dashed where no
 * floor plan documents it, with the passage glyph on each floor it is drawn on
 * and the equipment hanging on it. Read-only: everything comes from the risers
 * the floor plans carry. The network filter is the layer panel of the floor
 * plans (CompositePlanLayerPanel), its choice kept the same way under the
 * building.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from '@tanstack/react-router'
import { mdiLayersOutline } from '@mdi/js'
import { $api } from '../../api/queryClient'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { EmptyState } from '../ui/empty-state'
import { sanitizeNetworks } from '../plan-editor/engine/scene'
import { PLAN_COLORS } from '../plan-editor/engine/palette'
import { HEADER_HEIGHT, layoutRiserDiagram, LABEL_WIDTH, levelClickTarget, type RiserDiagram as Diagram } from './riser-diagram-layout'
import { CompositePlanLayerPanel, type CompositePlanNetworkFamily } from './CompositePlanLayerPanel'
import {
    DEFAULT_LAYERS,
    loadLayers,
    loadPanelOpen,
    saveLayers,
    savePanelOpen,
    type CompositePlanLayers,
    type VectorLayer,
} from './composite-plan-layers'

const RUNS_ONLY: VectorLayer[] = ['runs']

const RISERS_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/plan/risers'
const DISC = 7

export function RiserDiagram({ org, buildingUid }: { org: string; buildingUid: string }) {
    const { t } = useTranslation('assets')
    const query = $api.useQuery('get', RISERS_PATH, { params: { path: { orgCode: org, assetUid: buildingUid } } })
    if (query.isPending) {
        return <div className="h-48 animate-pulse rounded-lg border border-border bg-muted/40" data-testid="riser-diagram-loading" />
    }
    if (!query.data) return <EmptyState title={t('riserDiagram.error')} data-testid="riser-diagram-error" />
    return <RiserDiagramView org={org} buildingUid={buildingUid} diagram={query.data as Diagram} />
}

/**
 * The diagram itself, fed its payload: what the patterns page renders with
 * static data. Without `buildingUid` the layer choice is not kept.
 */
export function RiserDiagramView({ org, diagram, buildingUid }: { org: string; diagram: Diagram; buildingUid?: string }) {
    const { t, i18n } = useTranslation('assets')
    const navigate = useNavigate()

    const wrapRef = useRef<HTMLDivElement>(null)
    const [width, setWidth] = useState(640)
    useEffect(() => {
        const el = wrapRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        const ro = new ResizeObserver(([entry]) => setWidth(Math.max(280, Math.floor(entry.contentRect.width))))
        ro.observe(el)
        return () => ro.disconnect()
    }, [diagram])

    // The layer panel's state, in the floor plans' format: the runs layer
    // switch and the hidden network codes (a family is all its codes).
    const [layers, setLayers] = useState<CompositePlanLayers>(() => (buildingUid ? loadLayers(org, buildingUid) : DEFAULT_LAYERS))
    useEffect(() => {
        setLayers(buildingUid ? loadLayers(org, buildingUid) : DEFAULT_LAYERS)
    }, [org, buildingUid])
    const changeLayers = useCallback((next: CompositePlanLayers) => {
        setLayers(next)
        if (buildingUid) saveLayers(org, buildingUid, next)
    }, [org, buildingUid])
    const [panelOpen, setPanelOpen] = useState(loadPanelOpen)
    const togglePanel = () => {
        savePanelOpen(!panelOpen)
        setPanelOpen(!panelOpen)
    }

    const networks = useMemo(() => sanitizeNetworks(diagram.networks), [diagram])
    const networkOf = (code: string) => networks.find((n) => n.code === code)
    const labelOf = (code: string) => {
        const name = (networkOf(code)?.name ?? {}) as Record<string, string>
        return name[i18n.language] || name.en || name.fr || code
    }
    // The panel lists the networks the columns carry, by family in catalogue
    // order, each counted in columns.
    const panelNetworks = useMemo((): CompositePlanNetworkFamily[] => {
        const counts = new Map<string, number>()
        for (const c of diagram.columns) counts.set(c.network, (counts.get(c.network) ?? 0) + 1)
        const families: CompositePlanNetworkFamily[] = []
        const familyOf = (family: string) => {
            let f = families.find((x) => x.family === family)
            if (!f) {
                f = { family, label: t(`planEditor.network_families.${family}`, { defaultValue: family }), networks: [] }
                families.push(f)
            }
            return f
        }
        for (const n of networks) {
            const count = counts.get(n.code)
            if (!count) continue
            const name = (n.name ?? {}) as Record<string, string>
            familyOf(n.family || 'other').networks.push({ code: n.code, label: name[i18n.language] || name.en || name.fr || n.code, color: n.color, dash: n.dash, count })
            counts.delete(n.code)
        }
        for (const [code, count] of [...counts.entries()].sort(([x], [y]) => x.localeCompare(y))) {
            familyOf('other').networks.push({ code, label: code, count })
        }
        return families
    }, [diagram, networks, t, i18n.language])
    const hidden = useMemo(
        () => new Set(layers.vector.runs ? layers.hiddenNetworks : diagram.columns.map((c) => c.network)),
        [layers, diagram],
    )
    const layout = useMemo(() => layoutRiserDiagram({ ...diagram, networks }, width, hidden), [diagram, networks, width, hidden])

    if (!diagram.levels.length) {
        return <EmptyState title={t('riserDiagram.no_levels')} description={t('riserDiagram.no_levels_hint')} data-testid="riser-diagram-empty" />
    }

    // A floor opens its plan centred on the column nearest the click, or on
    // its default view when no visible column is drawn there.
    const openLevel = (levelUid: string, e: React.MouseEvent<SVGGElement>) => {
        const svg = e.currentTarget.ownerSVGElement
        const rect = svg?.getBoundingClientRect()
        const x = rect && rect.width > 0 ? ((e.clientX - rect.left) * layout.width) / rect.width : 0
        const at = levelClickTarget(layout, levelUid, x)
        void navigate({
            to: '/$org/assets/$assetUid/plan-editor',
            params: { org, assetUid: levelUid },
            search: at ? { center: `${Math.round(at.x)},${Math.round(at.y)}` } : {},
        })
    }
    const levelName = new Map(diagram.levels.map((l) => [l.asset_uid, l.name]))
    const bandOf = new Map(layout.bands.map((b) => [b.level.asset_uid, b]))

    return (
        <div className="flex flex-col gap-3" data-testid="riser-diagram">
            {panelNetworks.length > 0 && (
                <div className="flex justify-end">
                    <Button
                        variant={panelOpen ? 'secondary' : 'outline'}
                        size="sm"
                        aria-pressed={panelOpen}
                        data-testid="riser-diagram-layers-toggle"
                        onClick={togglePanel}
                    >
                        <AppIcon path={mdiLayersOutline} size="sm" className="mr-1" />
                        {t('planEditor.layers_title')}
                    </Button>
                </div>
            )}

            {diagram.columns.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="riser-diagram-no-columns">{t('riserDiagram.no_columns')}</p>
            )}

            <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
            {panelOpen && panelNetworks.length > 0 && (
                <CompositePlanLayerPanel
                    className="max-h-[320px] rounded-md border border-border bg-card lg:max-h-[640px] lg:w-72 lg:shrink-0"
                    underlays={[]}
                    vectorLayers={RUNS_ONLY}
                    layers={layers}
                    onChange={changeLayers}
                    networks={panelNetworks}
                />
            )}
            <div ref={wrapRef} className="w-full min-w-0 flex-1 rounded-lg border border-border bg-card">
                <svg
                    width={layout.width}
                    height={layout.height}
                    viewBox={`0 0 ${layout.width} ${layout.height}`}
                    className="block max-w-full"
                    role="img"
                    aria-label={t('riserDiagram.title')}
                >
                    {layout.bands.map((b, i) => (
                        <g key={b.level.asset_uid} data-testid={`riser-diagram-level-${b.level.asset_uid}`}
                            className="cursor-pointer" onClick={(e) => openLevel(b.level.asset_uid, e)}>
                            <title>{t('riserDiagram.open_level', { level: b.level.name })}</title>
                            <rect x={0} y={b.y} width={layout.width} height={b.height}
                                fill={i % 2 ? 'var(--muted)' : 'transparent'} fillOpacity={0.35} />
                            {/* The slab under the floor. */}
                            <line x1={0} x2={layout.width} y1={b.y + b.height} y2={b.y + b.height} stroke="var(--border)" />
                            <text x={8} y={b.mid} dominantBaseline="middle" fontSize={12} fill="var(--foreground)">
                                {b.level.name.length > 11 ? `${b.level.name.slice(0, 10)}…` : b.level.name}
                            </text>
                        </g>
                    ))}
                    <line x1={LABEL_WIDTH} x2={LABEL_WIDTH} y1={layout.bands[0]?.y ?? 0} y2={layout.height - 8} stroke="var(--border)" />

                    {layout.columns.map(({ column, number, x, segments }) => {
                        const color = networkOf(column.network)?.color ?? PLAN_COLORS.runFallback
                        const top = Math.min(...segments.map((s) => s.y1), ...column.levels.map((l) => bandOf.get(l.level_uid)?.mid ?? Infinity))
                        const openColumn = () => void navigate({ to: '/$org/assets/$assetUid', params: { org, assetUid: column.asset_uid } })
                        return (
                            <g key={column.asset_uid} data-testid={`riser-diagram-column-${column.asset_uid}`} data-network={column.network}>
                                <title>{`${column.name} — ${labelOf(column.network)}`}</title>
                                {segments.map((s, i) => (
                                    <line key={i} x1={x} x2={x} y1={s.y1} y2={s.y2} stroke={color} strokeWidth={3}
                                        strokeLinecap="round" strokeDasharray={s.dashed ? '5 5' : undefined}
                                        {...(s.gapLevelUid ? { 'data-testid': `riser-diagram-gap-${column.asset_uid}-${s.gapLevelUid}` } : {})}
                                        {...(s.dashed ? { 'data-dashed': 'true' } : {})} />
                                ))}
                                {/* A wide transparent stroke: the line is what opens the column. */}
                                {segments.length > 0 && (
                                    <line x1={x} x2={x} y1={Math.min(...segments.map((s) => s.y1))} y2={Math.max(...segments.map((s) => s.y2))}
                                        stroke="transparent" strokeWidth={16} className="cursor-pointer" onClick={openColumn}
                                        data-testid={`riser-diagram-column-hit-${column.asset_uid}`} />
                                )}
                                <g className="cursor-pointer" onClick={openColumn}>
                                    <circle cx={x} cy={Math.max(12, (Number.isFinite(top) ? top : HEADER_HEIGHT) - 22)} r={9} fill="var(--background)" stroke={color} strokeWidth={1.5} />
                                    <text x={x} y={Math.max(12, (Number.isFinite(top) ? top : HEADER_HEIGHT) - 22)} textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={600} fill="var(--foreground)">{number}</text>
                                </g>
                                {column.levels.map((l) => {
                                    const band = bandOf.get(l.level_uid)
                                    if (!band) return null
                                    const cy = band.mid
                                    return (
                                        <g key={l.level_uid}>
                                            {l.riser && (
                                                <g
                                                    className="cursor-pointer"
                                                    data-testid={`riser-diagram-riser-${column.asset_uid}-${l.level_uid}`}
                                                    data-passage={l.riser.passage}
                                                    onClick={() => void navigate({
                                                        to: '/$org/assets/$assetUid/plan-editor',
                                                        params: { org, assetUid: l.level_uid },
                                                        search: { center: `${Math.round(l.riser!.at.x)},${Math.round(l.riser!.at.y)}` },
                                                    })}
                                                >
                                                    <title>{t('riserDiagram.open_level', { level: levelName.get(l.level_uid) ?? '' })}</title>
                                                    <circle cx={x} cy={cy} r={DISC} fill={color} stroke="var(--background)" strokeWidth={1.5} />
                                                    {l.riser.passage === 'origin' ? (
                                                        <path d={`M ${x} ${cy - 4} L ${x + 4} ${cy + 3} L ${x - 4} ${cy + 3} Z`} fill="#ffffff" />
                                                    ) : l.riser.passage === 'terminal' ? (
                                                        <path d={`M ${x} ${cy + 4} L ${x + 4} ${cy - 3} L ${x - 4} ${cy - 3} Z`} fill="#ffffff" />
                                                    ) : (
                                                        <circle cx={x} cy={cy} r={2} fill="#ffffff" />
                                                    )}
                                                </g>
                                            )}
                                            {l.equipment.map((e, k) => (
                                                <g
                                                    key={e.uid}
                                                    className="cursor-pointer"
                                                    data-testid={`riser-diagram-equipment-${e.uid}`}
                                                    onClick={() => void navigate({ to: '/$org/assets/$assetUid', params: { org, assetUid: e.uid } })}
                                                >
                                                    <title>{e.name || e.type_code}</title>
                                                    <rect x={x + DISC + 4 + k * 11} y={cy - 4.5} width={9} height={9} rx={1.5}
                                                        fill="var(--background)" stroke={color} strokeWidth={1.5} />
                                                </g>
                                            ))}
                                        </g>
                                    )
                                })}
                            </g>
                        )
                    })}
                </svg>
            </div>
            </div>

            {layout.columns.length > 0 && (
                <ul className="grid gap-1 text-sm sm:grid-cols-2" data-testid="riser-diagram-legend">
                    {layout.columns.map(({ column, number }) => {
                        const network = networkOf(column.network)
                        const drawn = column.levels.filter((l) => l.riser).length
                        return (
                            <li key={column.asset_uid} className="flex min-w-0 items-start gap-2" data-testid={`riser-diagram-legend-${column.asset_uid}`}>
                                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold"
                                    style={{ borderColor: network?.color ?? PLAN_COLORS.runFallback }}>
                                    {number}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex min-w-0 items-center gap-2">
                                        <Link to="/$org/assets/$assetUid" params={{ org, assetUid: column.asset_uid }}
                                            className="truncate text-primary hover:underline" data-testid={`riser-diagram-legend-link-${column.asset_uid}`}>
                                            {column.name || column.asset_uid}
                                        </Link>
                                        {column.compliance_status && (
                                            <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                                                data-testid={`riser-diagram-compliance-${column.asset_uid}`}>
                                                {t(`riserDiagram.compliance.${column.compliance_status}`, { defaultValue: column.compliance_status })}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {labelOf(column.network)} · {t('riserDiagram.levels_drawn', { count: drawn })}
                                        {column.gaps.length > 0 && ` · ${t('riserDiagram.gaps', { count: column.gaps.length })}`}
                                    </div>
                                </div>
                            </li>
                        )
                    })}
                </ul>
            )}
        </div>
    )
}

